import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { kluczKraju } from '../kraj';

const SelectionContext = createContext(null);

export const selectionKey = (productType, id) => `${productType}:${id}`;

const COMPARE_KEY = kluczKraju('voltvalue_compare');
export const COMPARE_MAX = 4;
const FAVORITES_KEY = kluczKraju('voltvalue_favorites');
export const HISTORY_CHART_MAX = 2;

const loadCompare = () => {
  try {
    const raw = localStorage.getItem(COMPARE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.items)) {
        return { category: parsed.category ?? null, items: parsed.items };
      }
    }
  } catch (e) {
    // ignore corrupted storage
  }
  return { category: null, items: [] };
};

// Obserwowane trzymane jako Map<key, {product, productType}>; w localStorage jako tablica par.
const loadFavorites = () => {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return new Map(parsed);
    }
  } catch (e) {
    // ignore corrupted storage
  }
  return new Map();
};

export const SelectionProvider = ({ children }) => {
  // ----- Obserwowane (localStorage) -----
  const [favorites, setFavorites] = useState(loadFavorites);

  // Pusta lista = brak wpisu. Bez tego sam wjazd na stronę zapisywał w przeglądarce pusty
  // stan, zanim ktokolwiek czegokolwiek użył (audyt prywatności 2026-10-09).
  useEffect(() => {
    try {
      if (favorites.size === 0) localStorage.removeItem(FAVORITES_KEY);
      else localStorage.setItem(FAVORITES_KEY, JSON.stringify(Array.from(favorites.entries())));
    } catch (e) {
      // ignore
    }
  }, [favorites]);

  const toggleFavorite = useCallback((productType, product) => {
    const key = selectionKey(productType, product.id);
    setFavorites((prev) => {
      const map = new Map(prev);
      if (map.has(key)) map.delete(key);
      else map.set(key, { product, productType });
      return map;
    });
  }, []);

  const isFavorite = useCallback(
    (productType, id) => favorites.has(selectionKey(productType, id)),
    [favorites]
  );

  // ----- Historia ceny: maks. HISTORY_CHART_MAX otwartych okienek naraz -----
  // (stan scentralizowany, bo okienka są renderowane per-karta w listach —
  // bez wspólnego stanu każda karta wie tylko o sobie).
  const [openHistoryCharts, setOpenHistoryCharts] = useState([]);

  const openHistoryChart = useCallback((key) => {
    setOpenHistoryCharts((prev) => {
      if (prev.includes(key)) return prev;
      const next = [...prev, key];
      return next.length > HISTORY_CHART_MAX ? next.slice(next.length - HISTORY_CHART_MAX) : next;
    });
  }, []);

  const closeHistoryChart = useCallback((key) => {
    setOpenHistoryCharts((prev) => prev.filter((k) => k !== key));
  }, []);

  const isHistoryChartOpen = useCallback(
    (key) => openHistoryCharts.includes(key),
    [openHistoryCharts]
  );

  // ----- Porównanie (localStorage) -----
  const [compare, setCompare] = useState(loadCompare);

  useEffect(() => {
    try {
      if (!compare.category && compare.items.length === 0) localStorage.removeItem(COMPARE_KEY);
      else localStorage.setItem(COMPARE_KEY, JSON.stringify(compare));
    } catch (e) {
      // ignore
    }
  }, [compare]);

  // ----- Komunikaty (toast) -----
  const [notice, setNotice] = useState(null);
  const showNotice = useCallback((msg) => setNotice({ msg, ts: Date.now() }), []);
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  const isCompared = useCallback(
    (productType, id) => compare.items.some((it) => it.id === id && it.category === productType),
    [compare]
  );

  const toggleComparison = useCallback(
    (productType, product) => {
      const exists = compare.items.some((it) => it.id === product.id && it.category === productType);
      if (exists) {
        setCompare((prev) => {
          const items = prev.items.filter((it) => !(it.id === product.id && it.category === productType));
          return { category: items.length ? prev.category : null, items };
        });
        return 'removed';
      }
      if (compare.category && compare.category !== productType) {
        showNotice('Porównanie działa dla produktów z jednej kategorii. Najpierw wyczyść obecne porównanie.');
        return 'category_mismatch';
      }
      if (compare.items.length >= COMPARE_MAX) {
        showNotice(`Możesz porównać maksymalnie ${COMPARE_MAX} produkty naraz.`);
        return 'maxed';
      }
      setCompare((prev) => ({
        category: productType,
        items: [...prev.items, { ...product, category: productType }],
      }));
      return 'added';
    },
    [compare, showNotice]
  );

  const removeComparison = useCallback((id, category) => {
    setCompare((prev) => {
      const items = prev.items.filter((it) => !(it.id === id && it.category === category));
      return { category: items.length ? prev.category : null, items };
    });
  }, []);

  const clearComparison = useCallback(() => setCompare({ category: null, items: [] }), []);

  const value = useMemo(
    () => ({
      // Obserwowane
      toggleFavorite,
      isFavorite,
      favoritesList: Array.from(favorites.values()),
      favoritesCount: favorites.size,
      // Porównanie
      toggleComparison,
      isCompared,
      removeComparison,
      clearComparison,
      comparisonItems: compare.items,
      comparisonCategory: compare.category,
      comparisonCount: compare.items.length,
      // Historia ceny (max HISTORY_CHART_MAX otwartych okienek)
      openHistoryChart,
      closeHistoryChart,
      isHistoryChartOpen,
      // Toast
      notice,
      showNotice,
    }),
    [
      favorites, compare, notice,
      toggleFavorite, isFavorite, toggleComparison, isCompared,
      removeComparison, clearComparison, showNotice,
      openHistoryChart, closeHistoryChart, isHistoryChartOpen,
    ]
  );

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
};

export const useSelection = () => {
  const ctx = useContext(SelectionContext);
  if (!ctx) throw new Error('useSelection must be used within SelectionProvider');
  return ctx;
};
